import { memo, useState, useCallback, useRef } from "react";
import { TextField } from "@mui/material";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";

export interface EditableTextFieldProps {
  value: number;
  formattedValue: string;
  type: SingleValueCardType;
  displayColor: SingleValueCardColor;
  onValueChange: (value: number) => void;
}

const EditableTextField = memo(({
  value,
  formattedValue,
  type,
  displayColor,
  onValueChange,
}: EditableTextFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  
  const inputRef = useRef<HTMLInputElement>(null);
  const isCancelledRef = useRef(false);

  const handleFocus = useCallback(() => {
    if (!isEditing) {
      isCancelledRef.current = false;
      setIsEditing(true);
      setLocalValue(value.toString());
    }
  }, [isEditing, value]);

  const saveValue = useCallback(() => {
    const cleanString = localValue.replace(/[$,%+\s]/g, "");
    let parsedValue = parseFloat(cleanString);

    if (!isNaN(parsedValue)) {
      if (type === SingleValueCardType.CURRENCY) {
        parsedValue = parseFloat(parsedValue.toFixed(3)); 
      } else if (type === SingleValueCardType.PERCENTAGE) {
        parsedValue = parseFloat(parsedValue.toFixed(2)); 
      }

      if (parsedValue !== value) {
        onValueChange(parsedValue);
      }
    }
  }, [localValue, type, value, onValueChange]);

  const handleBlur = useCallback(() => {
    if (isEditing && !isCancelledRef.current) {
      saveValue();
    }
    setIsEditing(false);
  }, [isEditing, saveValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (isEditing) {
          saveValue();
          setIsEditing(false)
        }
      } else if (e.key === "Escape") {
        isCancelledRef.current = true;
        inputRef.current?.blur();
      } else if (!isEditing) {
          isCancelledRef.current = false;
          setIsEditing(true);
          handleFocus();
      }
    },
    [isEditing, saveValue, handleFocus]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  return (
    <TextField
      value={isEditing ? localValue : formattedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={handleFocus}
      onChange={handleChange}
      variant="standard"
      inputRef={inputRef}
      InputProps={{
        disableUnderline: true,
        sx: {
          fontSize: "1.5rem",
          fontWeight: 500,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          lineHeight: 1.2,
          color: (theme) => theme.palette[displayColor]?.main || displayColor,
          "& input": {
            textAlign: "left",
            padding: 0,
          },
        },
      }}
      sx={{
        "& .MuiInputBase-root": {
          fontSize: "1.5rem",
        },
      }}
    />
  );
});

EditableTextField.displayName = "EditableTextField";
export default EditableTextField;
